import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { EditorModule } from 'primeng/editor';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../services/session-storage.service';
import { ContractsService } from '../contracts.service';
import { ContractTemplate, ContractTemplateSection } from '../models/contract-drafting.model';

@Component({
  selector: 'app-contract-template-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    CheckboxModule,
    EditorModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageFormSectionComponent
  ],
  providers: [MessageService],
  templateUrl: './contract-template-form.component.html',
  styleUrls: ['./contract-template-form.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContractTemplateFormComponent implements OnInit {
  templateId?: number;
  template: ContractTemplate = { title: '', languageCode: 'en', version: 1, active: true };
  sections = signal<ContractTemplateSection[]>([]);
  saving = signal(false);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contractsService: ContractsService,
    private sessionStorageService: SessionStorageService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    this.template.companyId = this.sessionStorageService.getCompany()?.id;
    const id = Number(this.route.snapshot.paramMap.get('templateId'));
    if (Number.isFinite(id) && id > 0) {
      this.templateId = id;
      this.loadTemplate(id);
    } else {
      this.addDefaultSections();
    }
  }

  save() {
    this.saving.set(true);
    this.contractsService.saveContractTemplate(this.template).subscribe({
      next: template => {
        this.template = template;
        this.contractsService.saveContractTemplateSections(template.templateId!, this.sections()).subscribe(() => {
          this.saving.set(false);
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Contract template saved.' });
          this.router.navigate(['/contracts/templates']);
        });
      },
      error: () => {
        this.saving.set(false);
        this.messageService.add({ severity: 'error', summary: 'Save failed', detail: 'Could not save template.' });
      }
    });
  }

  addSection() {
    this.sections.update(sections => [
      ...sections,
      { sectionKey: 'custom_' + (sections.length + 1), sectionTitle: 'Custom Section', visible: true, required: false, sortOrder: (sections.length + 1) * 10, sectionHtml: '<p></p>' }
    ]);
  }

  private loadTemplate(id: number) {
    this.contractsService.getContractTemplates(this.template.companyId).subscribe(templates => {
      this.template = templates.find(template => template.templateId === id) || this.template;
    });
    this.contractsService.getContractTemplateSections(id).subscribe(sections => this.sections.set(sections || []));
  }

  private addDefaultSections() {
    this.sections.set([
      { sectionKey: 'scope', sectionTitle: 'Scope of Work', visible: true, required: true, sortOrder: 10, sectionHtml: '<p>{seller.companyName} and {buyer.companyName} agree to the scope described in this agreement.</p>' },
      { sectionKey: 'commercial_terms', sectionTitle: 'Commercial Terms', visible: true, required: true, sortOrder: 20, sectionHtml: '<p>Commercial terms are based on quotation {quotation.number}.</p>{rateCard.table}' },
      { sectionKey: 'payment_terms', sectionTitle: 'Payment Terms', visible: true, required: true, sortOrder: 30, sectionHtml: '<p>{revision.paymentTerms}</p>' },
      { sectionKey: 'signature_blocks', sectionTitle: 'Signature Blocks', visible: true, required: true, sortOrder: 40, sectionHtml: '<p>Seller (First Party): {signature.seller}</p><p>Buyer (Second Party): {signature.buyer}</p>' }
    ]);
  }
}
